import StatusBadge from './StatusBadge'
import ScoreBar from './ScoreBar'

const fmtRevenue = (v) => {
  if (!v) return '—'
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`
  if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`
  return `$${v}`
}

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—'

const fmtPhone = (p) => p || '—'

const fmtWebsite = (w) => {
  if (!w) return null
  try {
    const url = w.startsWith('http') ? w : `https://${w}`
    const display = new URL(url).hostname.replace('www.', '')
    return { url, display }
  } catch {
    return { url: w, display: w }
  }
}

const emailStatusIcon = (email_verified, email_status) => {
  if (email_verified === true)  return { icon: '✅', tip: email_status || 'Verified' }
  if (email_verified === false) return { icon: '❌', tip: email_status || 'Invalid' }
  if (email_verified === null && email_status) return { icon: '🟡', tip: email_status }
  return null
}

const phoneTypeIcon = (phone_type) => {
  if (phone_type === 'mobile') return { icon: '📱', tip: 'Mobile — SMS eligible' }
  return null
}

const COLUMNS = [
  { label: 'Company',      key: 'company',       width: 220 },
  { label: 'Contact',      key: 'contact',       width: 120 },
  { label: 'Email',        key: 'email',         width: 180 },
  { label: 'Phone',        key: 'phone',         width: 130 },
  { label: 'Website',      key: 'website',       width: 130 },
  { label: 'Industry',     key: 'industry',      width: 100 },
  { label: 'Score',        key: 'score',         width: 90  },
  { label: 'Status',       key: 'status',        width: 100 },
  { label: 'Source',       key: 'source',        width: 100 },
  { label: 'Last contact', key: 'last_contacted',width: 100 },
  { label: '',             key: 'actions',       width: 160 },
]

export default function ProspectsTable({ prospects, loading, onEdit, onDelete, onEnrich, enrichingIds, sortBy, sortDir, onSort }) {
  if (loading) {
    return <div style={styles.empty}>Loading prospects...</div>
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={styles.table}>
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                style={{ ...styles.th, width: col.width, cursor: col.key !== 'actions' ? 'pointer' : 'default' }}
                onClick={() => col.key !== 'actions' && onSort(col.key)}
              >
                {col.label}
                {sortBy === col.key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {prospects.length === 0 ? (
            <tr>
              <td colSpan={COLUMNS.length} style={styles.empty}>
                No prospects match your filters.
              </td>
            </tr>
          ) : (
            prospects.map((p) => {
              const isEnriching = enrichingIds?.has(p.id)
              const needsEnrich = !p.email || !p.contact
              const website = fmtWebsite(p.website)
              const emailIcon = emailStatusIcon(p.email_verified, p.email_status)
              const phoneIcon = phoneTypeIcon(p.phone_type)
              return (
                <tr key={p.id} style={styles.row}>
                  <td style={{ ...styles.td, fontWeight: 500, whiteSpace: 'normal' }} title={p.company}>{p.company}</td>
                  <td style={styles.td}>{p.contact || <span style={styles.missing}>—</span>}</td>
                  <td style={styles.td}>
                    {p.email ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <a href={`mailto:${p.email}`} style={styles.emailLink} title={p.email}>{p.email}</a>
                        {emailIcon && (
                          <span title={emailIcon.tip} style={{ fontSize: 12, cursor: 'default', flexShrink: 0 }}>
                            {emailIcon.icon}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span style={styles.missing}>—</span>
                    )}
                  </td>
                  <td style={{ ...styles.td, fontSize: 12 }}>
                    {p.phone ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <a href={`tel:${p.phone}`} style={styles.phoneLink}>{fmtPhone(p.phone)}</a>
                        {phoneIcon && (
                          <span title={phoneIcon.tip} style={{ fontSize: 12, cursor: 'default', flexShrink: 0 }}>
                            {phoneIcon.icon}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span style={styles.missing}>—</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {website
                      ? <a href={website.url} target="_blank" rel="noreferrer" style={styles.websiteLink} title={website.url}>{website.display}</a>
                      : <span style={styles.missing}>—</span>}
                  </td>
                  <td style={styles.td}>{p.industry || '—'}</td>
                  <td style={styles.td}><ScoreBar score={p.score} /></td>
                  <td style={styles.td}><StatusBadge status={p.status} /></td>
                  <td style={{ ...styles.td, ...styles.muted }}>{p.source}</td>
                  <td style={{ ...styles.td, ...styles.muted }}>{fmtDate(p.last_contacted)}</td>
                  <td style={styles.td}>
                    {needsEnrich && (
                      <button
                        style={{ ...styles.actionBtn, ...styles.enrichBtn }}
                        onClick={() => onEnrich(p)}
                        disabled={isEnriching}
                      >
                        {isEnriching ? '...' : '⚡ Enrich'}
                      </button>
                    )}
                    <button style={styles.actionBtn} onClick={() => onEdit(p)}>Edit</button>
                    <button style={{ ...styles.actionBtn, ...styles.deleteBtn }} onClick={() => onDelete(p)}>Del</button>
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}

const styles = {
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' },
  th: {
    textAlign: 'left', fontWeight: 500, fontSize: 11,
    color: '#888', padding: '8px 10px',
    borderBottom: '0.5px solid #e0dfd8', whiteSpace: 'nowrap',
    userSelect: 'none',
  },
  td: {
    padding: '10px 10px', borderBottom: '0.5px solid #e0dfd8',
    color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  row: { cursor: 'default' },
  muted: { color: '#888', fontSize: 12 },
  missing: { color: '#ccc' },
  emailLink: { color: '#185FA5', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis' },
  phoneLink: { color: '#444', textDecoration: 'none' },
  websiteLink: { color: '#0F6E56', textDecoration: 'none' },
  empty: { textAlign: 'center', padding: '2rem', color: '#888', fontSize: 13 },
  actionBtn: {
    background: 'none', border: '0.5px solid #ccc',
    borderRadius: 6, padding: '4px 8px', fontSize: 11,
    cursor: 'pointer', color: '#444', marginRight: 4,
  },
  enrichBtn: { borderColor: '#9FE1CB', color: '#0F6E56', background: '#E1F5EE' },
  deleteBtn: { borderColor: '#F0997B', color: '#993C1D' },
}